import { FOCUS_RING, HIT_SLOP, REDUCED_MOTION_SAFE } from "@sportsmet/ui";
import type { ReactNode } from "react";
import { useState } from "react";

import { openFanProfileLabel } from "../fanProfile/fanProfileCopy";
import { avatarPlateStyle } from "../profile/avatarPlateStyle";
import { initialsFromTitle } from "./chatDisplay";
import { PDF_FILE_NAME_FALLBACK } from "./chatExtrasCopy";
import type { typeRenderRow } from "./conversationListState";
import { GameCardBubble } from "./GameCardBubble";
import { GifBubble } from "./GifBubble";
import { LinkPreviewCard } from "./LinkPreviewCard";
import { MessageRunsText } from "./MessageRunsText";
import { foldMessageBody } from "./messageText";
import { MultiPhotoBubble } from "./MultiPhotoBubble";
import { PdfBubble } from "./PdfBubble";
import { PollBubble } from "./PollBubble";
import { reactionChipLabel } from "./reactorsLogic";

export type typeMessageBubbleProps = {
  row: Extract<typeRenderRow, { kind: "message" }>;
  sendState?: "sending" | "failed";
  onRetry?: () => void;
  onSelectAction: () => void;
  /**
   * ISSUE-1361. Opens "who reacted" on the tab of the chip that was clicked.
   * When set, each reaction chip is a button.
   */
  onOpenReactors?: (emoji: string) => void;
  /**
   * ISSUE-1356. Opens the sender's fan profile. When set, the avatar and the
   * name above another fan's message become buttons. The bubble stays free of
   * router imports so it renders in a plain test; the pane owns navigation.
   */
  onOpenSenderProfile?: () => void;
  /** R7. Only meaningful for a `poll` row; ignored otherwise. */
  onVote?: (optionIds: string[]) => void;
  isVoting?: boolean;
  voteFailed?: boolean;
};

export const MessageBubble = (props: typeMessageBubbleProps): ReactNode => {
  const { row, onOpenSenderProfile } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const isSending = props.sendState === "sending";

  // §5.9: fold first on the raw body, then render mention runs against the
  // folded text. bodyRuns already carries the full-text mention runs, so
  // this component re-derives its own folded runs from the raw text the
  // row's runs were parsed from.
  const rawBody = row.bodyRuns
    .map((run) => (run.kind === "text" ? run.value : `@${run.displayName}`))
    .join("");
  const fold = foldMessageBody(rawBody, isExpanded);
  const visibleRuns = fold.isFoldable
    ? [{ kind: "text" as const, value: fold.visibleText }]
    : row.bodyRuns;

  const tintClassName = row.isOwn ? "bg-primary-tint" : "bg-surface";

  // ISSUE-1356. The server already resolves `senderAvatarUrl`; the initials
  // plate stays the fallback for a fan without a photo.
  const senderAvatar =
    row.senderAvatarUrl !== null && row.senderAvatarUrl !== "" ? (
      <img
        src={row.senderAvatarUrl}
        alt=""
        data-testid="message-sender-photo"
        className="h-8 w-8 shrink-0 rounded-full object-cover"
      />
    ) : (
      <span
        aria-hidden={onOpenSenderProfile !== undefined ? true : undefined}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-caption"
        style={avatarPlateStyle(row.senderDisplayName)}
      >
        {initialsFromTitle(row.senderDisplayName)}
      </span>
    );

  let innerContent: ReactNode;
  if (
    row.messageKind === "image" &&
    row.mediaObjectIds !== null &&
    row.mediaObjectIds.length > 0
  ) {
    // R2: no card padding around a photo grid -- the images fill the
    // bubble's own rounded corners edge to edge, the caption (if any) gets
    // its own inset padding inside `MultiPhotoBubble`.
    innerContent = (
      <div
        className={`overflow-hidden rounded-md ${isSending ? "opacity-60" : ""}`}
      >
        <MultiPhotoBubble
          mediaObjectIds={row.mediaObjectIds}
          captionRuns={row.bodyRuns}
        />
      </div>
    );
  } else if (row.messageKind === "poll" && row.poll !== null) {
    innerContent = (
      <div
        className={`rounded-md px-4 py-2 ${tintClassName} ${isSending ? "opacity-60" : ""}`}
      >
        <PollBubble
          poll={row.poll}
          disabled={isSending}
          isVoting={props.isVoting ?? false}
          voteFailed={props.voteFailed ?? false}
          onVote={(optionIds) => {
            props.onVote?.(optionIds);
          }}
        />
      </div>
    );
  } else if (
    row.messageKind === "gameCard" &&
    row.gameCards !== null &&
    row.gameCards.length > 0
  ) {
    innerContent = (
      <div className={isSending ? "opacity-60" : ""}>
        <GameCardBubble cards={row.gameCards.map((c) => c.gameCard)} />
      </div>
    );
  } else if (row.messageKind === "gif" && row.gifObjectId !== null) {
    // R3. No card padding and no tint: the GIF fills its own rounded
    // corners the way a photo does. Before this branch a gif row fell
    // through to the text bubble below and drew an empty rectangle, since a
    // gif message carries no body.
    innerContent = (
      <div
        className={`overflow-hidden rounded-md ${isSending ? "opacity-60" : ""}`}
      >
        <GifBubble gifObjectId={row.gifObjectId} />
      </div>
    );
  } else if (row.messageKind === "pdf" && row.mediaObjectIds?.[0]) {
    // ISSUE-750 R5/R6. mediaObjectIds always carries exactly one id for a
    // real pdf row: messaging-api's insertPdfMessage always writes one, and
    // /message/list passes the column through unchanged. `row.pdf` falls
    // back to PDF_FILE_NAME_FALLBACK so a malformed response still draws a
    // legible row rather than falling through to the plain-text branch
    // below and reproducing the empty bubble this card exists to fix.
    innerContent = (
      <div
        className={`rounded-md ${tintClassName} ${isSending ? "opacity-60" : ""}`}
      >
        <PdfBubble
          mediaObjectId={row.mediaObjectIds[0]}
          pdf={row.pdf ?? { fileName: PDF_FILE_NAME_FALLBACK, byteSize: null }}
        />
      </div>
    );
  } else {
    // text, and any kind this client does not yet draw its own bubble for:
    // the original text-run rendering, unchanged. A gif row reaches here
    // only when its `gifObjectId` is missing, which is a malformed
    // response rather than an ordinary case.
    innerContent = (
      <div
        className={`rounded-md px-4 py-2 ${tintClassName} ${isSending ? "opacity-60" : ""}`}
      >
        {row.replyToPreview !== null ? (
          <div className="mb-1 border-l-2 border-hairline pl-2 text-caption text-ink-tertiary">
            {row.replyToPreview}
          </div>
        ) : null}
        <p className="text-body text-ink">
          <MessageRunsText runs={visibleRuns} />
        </p>
        {fold.isFoldable ? (
          <button
            type="button"
            onClick={() => {
              setIsExpanded((prev) => !prev);
            }}
            className="text-caption text-primary"
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        ) : null}
        {row.isEdited ? (
          <p className="text-caption text-ink-tertiary">Edited</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-testid="conversation-message-row"
      data-message-id={row.messageId}
      className={`flex ${row.showSenderHeader ? "mt-2" : "mt-1"} ${row.isOwn ? "justify-end" : "justify-start"} gap-2 transition-colors duration-500 data-[highlighted=true]:bg-warning-tint ${REDUCED_MOTION_SAFE}`}
    >
      {!row.isOwn && row.showSenderHeader ? (
        onOpenSenderProfile !== undefined ? (
          <button
            type="button"
            data-testid="message-sender-avatar"
            aria-label={openFanProfileLabel(row.senderDisplayName)}
            onClick={onOpenSenderProfile}
            className={`h-8 w-8 shrink-0 rounded-full ${FOCUS_RING}`}
          >
            {senderAvatar}
          </button>
        ) : (
          senderAvatar
        )
      ) : !row.isOwn ? (
        <span className="w-8 shrink-0" />
      ) : null}
      <div className="flex max-w-[78%] flex-col gap-1">
        {!row.isOwn && row.showSenderHeader ? (
          onOpenSenderProfile !== undefined ? (
            <button
              type="button"
              data-testid="message-sender-name"
              aria-label={openFanProfileLabel(row.senderDisplayName)}
              onClick={onOpenSenderProfile}
              className={`self-start text-left text-caption text-ink-tertiary underline-offset-2 hover:underline ${FOCUS_RING}`}
            >
              {row.senderDisplayName}
            </button>
          ) : (
            <span className="text-caption text-ink-tertiary">
              {row.senderDisplayName}
            </span>
          )
        ) : null}
        {/* The actions button sits beside the bubble, on its outer side,
            never over it: drawn on top it covered the end of the first line,
            so a tap on a link's last letters opened the menu (QA round 1). */}
        <div
          className={`relative flex items-start gap-1 ${row.isOwn ? "flex-row-reverse" : ""}`}
          data-testid="message-bubble-line"
        >
          <div className="min-w-0">{innerContent}</div>
          <button
            type="button"
            aria-label="Message actions"
            onClick={props.onSelectAction}
            className="flex min-h-touch-target min-w-touch-target shrink-0 items-center justify-center text-caption text-ink-tertiary"
          >
            ···
          </button>
        </div>
        {row.linkPreview !== null && row.messageKind === "text" ? (
          <LinkPreviewCard preview={row.linkPreview} isOwn={row.isOwn} />
        ) : null}
        {row.reactions.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.reactions.map((reaction) => {
              const chipClassName = `rounded-full px-2 text-caption ${
                reaction.reactedByMe ? "bg-primary-tint" : "bg-surface"
              }`;
              const onOpenReactors = props.onOpenReactors;
              return onOpenReactors !== undefined ? (
                <button
                  key={reaction.emoji}
                  type="button"
                  data-testid={`reaction-chip-${reaction.emoji}`}
                  aria-label={reactionChipLabel(reaction.emoji, reaction.count)}
                  onClick={() => {
                    onOpenReactors(reaction.emoji);
                  }}
                  className={`${chipClassName} relative min-w-touch-target hover:bg-surface-secondary ${FOCUS_RING} ${HIT_SLOP}`}
                >
                  {reaction.emoji} {reaction.count}
                </button>
              ) : (
                <span key={reaction.emoji} className={chipClassName}>
                  {reaction.emoji} {reaction.count}
                </span>
              );
            })}
          </div>
        ) : null}
        {props.sendState === "failed" ? (
          <button
            type="button"
            onClick={props.onRetry}
            className="text-caption text-error"
          >
            Could not send · Try again
          </button>
        ) : row.timeLabel !== null ? (
          <span className="text-caption text-ink-tertiary">
            {row.timeLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};
